import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Context } from "../../index";
import Settings from "../../assets/settings.svg";
import Notification from "../../assets/notification.svg";
import Avatar, { AvatarSize } from "../Shared/Avatar/Avatar";
import "../Header/Header.css";
import { Link, useNavigate } from "react-router-dom";
import { BreadCrumb } from "primereact/breadcrumb";
import { AppContext } from "../../App";
import { MenuItem } from "primereact/menuitem";
import SettingsPopup from "../PersonalSettings/SettingsPopup/SettingsPopup";
import PersonalSettings from "../PersonalSettings/PersonalSettings/PersonalSettings";
import PasswordChange from "../PersonalSettings/PasswordChangePopup/PasswordChange";
import Button from "../UiLib/Button/Button";
import Notifications from "../Shared/Notifications/Notifications";
import { ISearchResult } from "../../common/api_interfaces/search/ISearchResult";
import { isAxiosError } from "axios";
import AnimatedSearch from "../UiLib/AnimatedSearch/AnimatedSearch";
import LightSearchPreview from "../Shared/LightSearchPreview/LightSearchPreview";
import { useSocketContext } from "../../contexts/SocketContext";
import { hasPermission } from "../../helpers/hasPermisson";
import { IFullUserInfo, IUserInfo } from "../../common/api_interfaces/user/IUserInfo";

const Header = () => {
    const { store } = useContext(Context);
    const { breadCrumb, systemUserAccess } = useContext(AppContext);
    const { notification } = useSocketContext();
    const { showToast } = useContext(AppContext);
    const navigate = useNavigate();
    
    const [showSettingsPopup, setShowSettingsPopup] = useState<boolean>(false);
    const [showSettingsPopupLock, setShowSettingsPopupLock] = useState<boolean>(false);
    const [showSettingsMenu, setShowSettingsMenu] = useState<boolean>(false);
    const [showPasswordChangeMenu, setPasswordChangeMenu] = useState<boolean>(false);
    const [breadCrumbMenuItem, setBreadCrumbMenuItem] = useState<MenuItem[]>([]);
    const [showNotifications, setShowNotifications] = useState(false);
    const [searchText, setSearchText] = useState<string>("");
    const [dataForView, setDataForView] = useState<ISearchResult>();
    const [showAllSearch, setShowAllSearch] = useState<boolean>(false);
    const isInitialMount = useRef(true);
    const [user, setUser] = useState<IUserInfo>(store.user);
    const prevCountNotViewedRef = useRef(0);

    const viewSystemSettings = useMemo(() => {
        return (
            hasPermission(systemUserAccess, "SystemAction", "view") ||
            hasPermission(systemUserAccess, "LicenseAction", "view") ||
            hasPermission(systemUserAccess, "LicenseAction", "edit")
        );
    }, [systemUserAccess]);

    const countNotViewed = useMemo(() => 
        notification?.data?.countNotViewedData ?? 0, 
        [notification?.data?.countNotViewedData]
    );

    useEffect(() => {
        setUser(store.user)
    }, [store.user])

   useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            prevCountNotViewedRef.current = countNotViewed;
            return;
        }

        if (countNotViewed > prevCountNotViewedRef.current && prevCountNotViewedRef.current !== 0) {
            const newNotificationsCount = countNotViewed - prevCountNotViewedRef.current;
            showToast(`Вас упомянули. Проверьте уведомления.`);
        }

        prevCountNotViewedRef.current = countNotViewed;
    }, [countNotViewed, showToast]);

    const breadCrumbMemo = useMemo(() => breadCrumb, [breadCrumb]);

    useEffect(() => {
        const newBreadCrumbMenuItem: MenuItem[] = breadCrumbMemo.map((b) => ({
            label: b.label ?? " ",
            url: b.url,
            template: <Link to={b.url}>{b.label ?? " "}</Link>,
        }));

        setBreadCrumbMenuItem(newBreadCrumbMenuItem);
    }, [breadCrumbMemo]);

    const searchTextMemo = useMemo(() => searchText.toLowerCase(), [searchText]);

    useEffect(() => {
        const searchLight = async () => {
            if (!searchTextMemo) {
                setDataForView(undefined);
                return;
            }

            try {
                const res = await store.searchService.lightSearch(searchTextMemo);
                
                if (res.data && (
                    res.data.boards.length > 0 ||
                    res.data.tasks.length > 0 ||
                    res.data.projects.length > 0
                )) {
                    setDataForView(res.data);
                } else {
                    setDataForView(undefined);
                }
            } catch (err) {
                if (isAxiosError(err)) {
                    console.error(err.response?.data.message);
                }
                setDataForView(undefined);
            }
        };

        const timeoutId = setTimeout(searchLight, 300); // Debounce поиска
        return () => clearTimeout(timeoutId);
    }, [searchTextMemo, store.searchService]);

    const goToSettings = useCallback(() => {
        navigate("/system/settings");
    }, [navigate]);

    const closeSettingsPopup = useCallback(() => {
        setShowSettingsPopupLock(true);
        setTimeout(() => setShowSettingsPopupLock(false), 300);
        setShowSettingsPopup(false);
    }, []);

    const closeNotification = useCallback(() => {
        setShowNotifications(false);
    }, []);

    const onSubmitSearchFunc = useCallback(() => {
        navigate("/globalsearch", { state: { searchText } });
    }, [navigate, searchText]);

    const handleSettingsClick = useCallback(() => {
        setShowSettingsMenu(true);
        setShowSettingsPopup(false);
    }, []);

    const handlePasswordChangeClick = useCallback(() => {
        setPasswordChangeMenu(true);
        setShowSettingsPopup(false);
    }, []);

    const toggleSettingsPopup = useCallback(() => {
        if (!showSettingsPopupLock) {
            setShowSettingsPopup(prev => !prev);
        }
    }, [showSettingsPopupLock]);

    const toggleNotifications = useCallback(() => {
        setShowNotifications(prev => !prev);
    }, []);

    const handleSearchTextChange = useCallback((text: string) => {
        setSearchText(text);
    }, []);

    const handleStashChange = useCallback((stash: boolean) => {
        setShowAllSearch(!stash);
    }, []);

    const handlePreviewClose = useCallback(() => {
        setShowAllSearch(false);
    }, []);

    return (
        <div className="header-component">
            <div className="headName">
                <BreadCrumb model={breadCrumbMenuItem} />
            </div>
            <div id="right-control-panel">
                <AnimatedSearch
                    onSubmitFunc={onSubmitSearchFunc}
                    changeSearchValue={handleSearchTextChange}
                    swapFuncButton={true}
                    showPreviewList={!!dataForView}
                    placeholder={"Поиск..."}
                    value={searchText}
                    widthInput={455}
                    stashDate={showAllSearch}
                    callBackStash={handleStashChange}
                >
                    <LightSearchPreview 
                        callBack={handlePreviewClose} 
                        dataForView={dataForView} 
                    />
                </AnimatedSearch>
                {viewSystemSettings && (
                    <Button
                        callBack={goToSettings}
                        style={{ margin: "2px" }}
                        image={Settings}
                        className={"right-control-panel-settings"}
                    />
                )}
                <Button
                    callBack={toggleNotifications}
                    style={{ margin: "2px" }}
                    image={Notification}
                    buttonSize="small"
                    className={"right-control-panel-notification"}
                />
                {countNotViewed > 0 && (
                    <span className="right-control-panel-notification-num">
                        {countNotViewed}
                    </span>
                )}
                <div
                    className="user-profile"
                    onClick={toggleSettingsPopup}
                >
                    <div className="header__component-text">
                        <p>{user.name} {user.surname}</p>
                    </div>
                    <Avatar userId={user.id} size={AvatarSize.lg} />
                </div>
                {showNotifications && (
                    <div className="header-notification-popup-container">
                        <Notifications onClose={closeNotification} />
                    </div>
                )}
                {showSettingsPopup && (
                    <div className="header-settings-popup-container">
                        <SettingsPopup
                            onClose={closeSettingsPopup}
                            onSettingsClick={handleSettingsClick}
                            onPasswordChangeClick={handlePasswordChangeClick}
                        />
                    </div>
                )}
                {showSettingsMenu && (
                    <div className="header-settings-popup-container">
                        <PersonalSettings onClose={() => setShowSettingsMenu(false)} />
                    </div>
                )}
                {showPasswordChangeMenu && (
                    <div className="header-settings-popup-container">
                        <PasswordChange onClose={() => setPasswordChangeMenu(false)} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default observer(Header);
